<template>
    <div style="padding: 24px">
        <a-card :bordered="false" title="广告配置">
            <!-- 搜索栏 -->
            <div class="toolbar">
                <a-input-number v-model:value="queryParams.ad_id" placeholder="广告ID" style="width: 140px" :min="1" />
                <a-input v-model:value="queryParams.source_code" placeholder="来源编码" style="width: 160px" allow-clear />
                <a-select v-model:value="queryParams.status" placeholder="状态" style="width: 100px" allow-clear>
                    <a-select-option :value="1">启用</a-select-option>
                    <a-select-option :value="0">禁用</a-select-option>
                </a-select>
                <a-button type="primary" @click="fetchList">查询</a-button>
                <a-button @click="resetQuery">重置</a-button>
                <a-button type="primary" style="margin-left: auto" @click="openCreate">新增配置</a-button>
            </div>

            <a-table :columns="columns" :data-source="list" :loading="loading" row-key="id"
                :pagination="{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }" :expand-row-by-click="false"
                v-model:expandedRowKeys="expandedRowKeys">
                <template #bodyCell="{ column, record }">
                    <template v-if="column.dataIndex === 'status'">
                        <a-tag :color="record.status === 1 ? 'green' : 'red'">{{ record.status === 1 ? '启用' : '禁用'
                            }}</a-tag>
                    </template>
                    <template v-if="column.dataIndex === 'routes_count'">
                        <a-tag color="blue">{{ (record.routes || []).length }} 条路由</a-tag>
                    </template>
                    <template v-if="column.dataIndex === 'action'">
                        <a-space>
                            <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
                            <a-button type="link" size="small" :danger="record.status === 1"
                                @click="toggleStatus(record)">{{ record.status === 1 ? '禁用' : '启用' }}</a-button>
                            <a-tooltip title="复制投放链接">
                                <a-button type="link" size="small" style="color: #1677ff"
                                    @click="copyLandingUrl(record)">
                                    🔗 复制链接
                                </a-button>
                            </a-tooltip>
                        </a-space>
                    </template>
                </template>

                <!-- 展开行：展示 routes 列表（默认全部展开） -->
                <template #expandedRowRender="{ record }">
                    <a-table :columns="routeColumns" :data-source="record.routes || []" row-key="id" size="small"
                        :pagination="false">
                        <template #bodyCell="{ column: rc, record: r }">
                            <template v-if="rc.dataIndex === 'city_list'">
                                <span>{{ formatCityList(r) }}</span>
                            </template>
                            <template v-if="rc.dataIndex === 'require_real_china_ip'">
                                <a-tag :color="r.require_real_china_ip ? 'orange' : 'default'">{{
                                    r.require_real_china_ip ? '是' : '否' }}</a-tag>
                            </template>
                            <template v-if="rc.dataIndex === 'required_query_keywords'">
                                <span
                                    v-if="!r.required_query_keywords || r.required_query_keywords.length === 0">-</span>
                                <a-tag v-else v-for="kw in r.required_query_keywords" :key="kw" color="purple">{{ kw
                                    }}</a-tag>
                            </template>
                            <template v-if="rc.dataIndex === 'status'">
                                <a-tag :color="r.status === 1 ? 'green' : 'red'">{{ r.status === 1 ? '启用' : '禁用'
                                    }}</a-tag>
                            </template>
                        </template>
                    </a-table>
                </template>
            </a-table>
        </a-card>

        <!-- 新增/编辑弹窗 -->
        <a-modal v-model:open="modalVisible" :title="editingConfig ? '编辑广告配置' : '新增广告配置'" :confirm-loading="saving"
            width="960px" @ok="handleSave" @cancel="resetForm">
            <a-form :model="form" :label-col="{ span: 4 }" :wrapper-col="{ span: 19 }">
                <a-divider orientation="left">基础信息</a-divider>
                <a-form-item label="配置名称">
                    <a-input v-model:value="form.name" placeholder="如：default-ad-config" />
                </a-form-item>
                <a-form-item label="广告ID" required>
                    <a-input-number v-model:value="form.ad_id" :min="1" style="width: 100%" placeholder="广告配置表 ID" />
                </a-form-item>
                <a-form-item label="来源编码">
                    <a-input v-model:value="form.source_code" placeholder="为空不限制" allow-clear />
                </a-form-item>
                <a-form-item label="国家">
                    <a-input v-model:value="form.country" placeholder="为空不限制" allow-clear />
                </a-form-item>
                <a-form-item label="省份">
                    <a-input v-model:value="form.province" placeholder="为空不限制" allow-clear />
                </a-form-item>
                <a-form-item label="运营商">
                    <a-input v-model:value="form.isp" placeholder="为空不限制" allow-clear />
                </a-form-item>
                <a-form-item label="优先级">
                    <a-input-number v-model:value="form.priority" :min="0" style="width: 100%"
                        placeholder="多份配置同时命中时优先级" />
                </a-form-item>
                <a-form-item label="状态">
                    <a-switch v-model:checked="formStatusChecked" checked-children="启用" un-checked-children="禁用" />
                </a-form-item>

                <a-divider orientation="left">
                    城市路由
                    <a-button type="link" size="small" @click="addRoute">+ 添加路由</a-button>
                </a-divider>
                <a-alert message="路由按 priority 从高到低匹配；禁止城市列表内的城市不会命中该路由，留空表示不排除任何城市（兑底）。" type="info" show-icon
                    style="margin-bottom: 12px" />

                <div v-for="(route, idx) in form.routes" :key="idx" class="route-card">
                    <div class="route-card-header">
                        <span class="route-card-title">路由 #{{ idx + 1 }}</span>
                        <a-popconfirm title="删除该路由？" ok-text="确认" cancel-text="取消" @confirm="removeRoute(idx)">
                            <a-button type="link" size="small" danger>删除</a-button>
                        </a-popconfirm>
                    </div>
                    <a-form-item label="路由名称" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
                        <a-input v-model:value="route.name" placeholder="如：beijing-shanghai" />
                    </a-form-item>
                    <a-form-item label="禁止城市" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
                        <a-select v-model:value="route.city_list" mode="tags"
                            placeholder="输入要排除的城市名后回车；列表内城市不会命中本路由；留空不排除" allow-clear style="width: 100%" />
                    </a-form-item> <a-form-item label="真实国内IP" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
                        <a-switch :checked="!!route.require_real_china_ip" checked-children="要求"
                            un-checked-children="不要求" @change="(v) => (route.require_real_china_ip = !!v)" />
                        <span style="margin-left: 8px; color: #999; font-size: 12px">开启后仅公网且归属中国的 IP 才命中本路由</span>
                    </a-form-item>
                    <a-form-item label="必含关键字" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
                        <a-select v-model:value="route.required_query_keywords" mode="tags"
                            placeholder="输入关键字后回车，例如 clickid；不区分大小写；留空不限制" allow-clear style="width: 100%" />
                    </a-form-item> <a-form-item label="目标落地页" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }"
                        required>
                        <a-input v-model:value="route.target_url" placeholder="https://landing.example.com" />
                    </a-form-item>
                    <a-row>
                        <a-col :span="12">
                            <a-form-item label="优先级" :label-col="{ span: 10 }" :wrapper-col="{ span: 14 }">
                                <a-input-number v-model:value="route.priority" :min="0" style="width: 100%" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item label="权重" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
                                <a-input-number v-model:value="route.weight" :min="0" style="width: 100%"
                                    placeholder="0 不参与分流" />
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-form-item label="状态" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
                        <a-switch :checked="route.status === 1" checked-children="启用" un-checked-children="禁用"
                            @change="(v) => (route.status = v ? 1 : 0)" />
                    </a-form-item>
                </div>

                <div v-if="form.routes.length === 0" class="empty-routes">
                    暂无路由，请点击上方"+ 添加路由"
                </div>
            </a-form>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import {
    listAdConfigs,
    createAdConfig,
    updateAdConfig,
    enableAdConfig,
    disableAdConfig,
} from "@/utils/adApi";

/* ---------- 查询 ---------- */
const queryParams = reactive({ ad_id: null, source_code: "", status: null });

const resetQuery = () => {
    queryParams.ad_id = null;
    queryParams.source_code = "";
    queryParams.status = null;
    fetchList();
};

/* ---------- 列表 ---------- */
const loading = ref(false);
const list = ref([]);
const expandedRowKeys = ref([]);

const columns = [
    { title: "ID", dataIndex: "id", width: 80 },
    { title: "配置名称", dataIndex: "name", width: 180 },
    { title: "广告ID", dataIndex: "ad_id", width: 90 },
    { title: "来源编码", dataIndex: "source_code", width: 120 },
    { title: "优先级", dataIndex: "priority", width: 80 },
    { title: "路由数", dataIndex: "routes_count", width: 100 },
    { title: "状态", dataIndex: "status", width: 80 },
    { title: "操作", dataIndex: "action", width: 220 },
];

const routeColumns = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "路由名称", dataIndex: "name", width: 160 },
    { title: "禁止城市", dataIndex: "city_list" },
    { title: "真实国内IP", dataIndex: "require_real_china_ip", width: 110 },
    { title: "必含关键字", dataIndex: "required_query_keywords", width: 160 },
    { title: "目标落地页", dataIndex: "target_url", ellipsis: true },
    { title: "优先级", dataIndex: "priority", width: 80 },
    { title: "权重", dataIndex: "weight", width: 70 },
    { title: "状态", dataIndex: "status", width: 80 },
];

const formatCityList = (r) => {
    const list = Array.isArray(r.city_list) ? r.city_list : [];
    if (list.length === 0 && !r.city) return "不排除（兑底）";
    if (list.length > 0) return list.join("、");
    return r.city || "-";
};

const fetchList = async () => {
    loading.value = true;
    try {
        const params = {};
        if (queryParams.ad_id) params.ad_id = queryParams.ad_id;
        if (queryParams.source_code) params.source_code = queryParams.source_code;
        if (queryParams.status != null) params.status = queryParams.status;
        const json = await listAdConfigs(params);
        list.value = Array.isArray(json?.items) ? json.items : [];
        // 默认全部展开，方便一眼查看路由配置
        expandedRowKeys.value = list.value.map((r) => r.id);
    } catch {
        message.error("查询失败");
    } finally {
        loading.value = false;
    }
};

onMounted(fetchList);

/* ---------- 表单 ---------- */
const modalVisible = ref(false);
const saving = ref(false);
const editingConfig = ref(null);

const emptyRoute = () => ({
    name: "",
    city_list: [],
    require_real_china_ip: false,
    required_query_keywords: [],
    target_url: "",
    priority: 100,
    weight: 100,
    status: 1,
});

const emptyForm = () => ({
    name: "",
    ad_id: null,
    source_code: "",
    country: "",
    province: "",
    isp: "",
    priority: 100,
    status: 1,
    routes: [],
});

const form = reactive(emptyForm());

const formStatusChecked = computed({
    get: () => form.status === 1,
    set: (v) => {
        form.status = v ? 1 : 0;
    },
});

const openCreate = () => {
    editingConfig.value = null;
    Object.assign(form, emptyForm());
    form.routes = [emptyRoute()];
    modalVisible.value = true;
};

const openEdit = (record) => {
    editingConfig.value = record;
    Object.assign(form, {
        ...emptyForm(),
        ...record,
        routes: Array.isArray(record.routes)
            ? record.routes.map((r) => ({
                ...emptyRoute(),
                ...r,
                city_list: Array.isArray(r.city_list) ? [...r.city_list] : [],
                required_query_keywords: Array.isArray(r.required_query_keywords)
                    ? [...r.required_query_keywords]
                    : [],
            }))
            : [],
    });
    modalVisible.value = true;
};

const resetForm = () => {
    editingConfig.value = null;
    Object.assign(form, emptyForm());
};

const addRoute = () => {
    form.routes.push(emptyRoute());
};

const removeRoute = (idx) => {
    form.routes.splice(idx, 1);
};

const handleSave = async () => {
    if (!form.ad_id) {
        message.warning("请填写广告ID");
        return;
    }
    if (!form.routes || form.routes.length === 0) {
        message.warning("至少添加一条路由");
        return;
    }
    for (let i = 0; i < form.routes.length; i++) {
        if (!form.routes[i].target_url) {
            message.warning(`路由 #${i + 1} 缺少目标落地页`);
            return;
        }
    }
    saving.value = true;
    try {
        if (editingConfig.value) {
            await updateAdConfig(editingConfig.value.id, form);
            message.success("更新成功");
        } else {
            await createAdConfig(form);
            message.success("创建成功");
        }
        modalVisible.value = false;
        resetForm();
        fetchList();
    } catch {
        message.error("保存失败");
    } finally {
        saving.value = false;
    }
};

/* ---------- 启用/禁用 ---------- */
const toggleStatus = async (record) => {
    try {
        if (record.status === 1) {
            await disableAdConfig(record.id);
            message.success("已禁用");
        } else {
            await enableAdConfig(record.id);
            message.success("已启用");
        }
        fetchList();
    } catch {
        message.error("操作失败");
    }
};

/* ---------- 复制投放链接 ---------- */
const copyLandingUrl = (record) => {
    const url = `https://ad-router.intbull.com/api/v1/landing_page?ad_id=${record.ad_id}`;
    navigator.clipboard.writeText(url).then(() => {
        message.success("投放链接已复制");
    }).catch(() => {
        message.error("复制失败，请手动复制：" + url);
    });
};
</script>

<style scoped>
.toolbar {
    margin-bottom: 16px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
}

.route-card {
    border: 1px solid #eceef2;
    border-radius: 6px;
    padding: 12px 16px 0 16px;
    margin-bottom: 12px;
    background: #fafbfc;
}

.route-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.route-card-title {
    font-weight: 600;
    color: #333;
}

.empty-routes {
    text-align: center;
    color: #999;
    padding: 24px 0;
}
</style>
